// ---------------------------------------------------------------------------
// Platform statistics dashboard. Progressive enhancement for the
// /Admin/Statistics shell. Feature-
// detects the page root and no-ops elsewhere, so it is safe to wire globally
// in main.ts.
//
// Each widget fetches its own JSON endpoint under data-endpoint-base
// (/Admin/statistics) INDEPENDENTLY (per-widget): a failing widget shows
// its own [data-error] without taking the others down. Charts are
// rendered with Chart.js, bundled into app.js by esbuild — no CDN, no inline
// script (CSP default-src 'self'); all data arrives via fetch JSON.
// ---------------------------------------------------------------------------

import {
  Chart,
  LineController,
  BarController,
  DoughnutController,
  LineElement,
  PointElement,
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  Legend,
  Tooltip,
  Filler,
} from "chart.js";
import type { ChartConfiguration, ChartDataset } from "chart.js";

// Register only the pieces the five charts need (keeps the bundle lean).
Chart.register(
  LineController,
  BarController,
  DoughnutController,
  LineElement,
  PointElement,
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  Legend,
  Tooltip,
  Filler,
);

// --- Response shapes (camelCase mirrors the statistics endpoints) -----------

interface TimeBucketPoint {
  bucketStartUtc: string;
  currency: string;
  amount: number;
  count: number;
}

interface CurrencyKpis {
  currency: string;
  activePayers: number;
  mrr: number;
  arpu: number;
  grossRevenueInPeriod: number;
  newCustomers: number;
  newSubscriptions: number;
  canceledSubscriptions: number;
}

interface PriceBreakdownRow {
  priceId: string;
  productName: string | null;
  unitAmount: number;
  interval: string | null;
  activeCount: number;
  mrrContribution: number;
}

interface ConnectionRevenueRow {
  unionId: string;
  slug: string | null;
  currency: string;
  revenueInPeriod: number;
}

interface SummaryResponse {
  kpis: CurrencyKpis[];
  activeConnectionCount: number;
  partialFailures: string[];
  historyComplete: boolean;
}

interface SubscriptionsResponse {
  newSubscriptions: TimeBucketPoint[];
  canceledSubscriptions: TimeBucketPoint[];
  breakdown: PriceBreakdownRow[];
}

// --- Constants --------------------------------------------------------------

// Widget values double as endpoint slugs (the data-widget contract).
const WIDGETS = [
  "summary",
  "revenue",
  "subscriptions",
  "customers",
  "revenue-by-connection",
] as const;

// Stable per-currency / per-series colours (indigo, coral, teal, …).
const PALETTE = [
  "#2e2a8c",
  "#f26b5e",
  "#1e9e88",
  "#9a6a12",
  "#2b4f9a",
  "#c0392b",
  "#1d7a52",
];

// KPI keys whose value is a money amount (minor units → format per currency).
const MONEY_KPIS = new Set(["mrr", "arpu", "gross-revenue"]);
// KPI keys rendered once per currency (money + per-currency counts).
const PER_CURRENCY_KPIS = new Set([
  "active-payers",
  "mrr",
  "arpu",
  "gross-revenue",
  "new-subscriptions",
  "churn",
]);

// Live Chart instances keyed by canvas data-chart value (for destroy/replace).
const charts = new Map<string, Chart>();
// Cached KPI card templates keyed by their grid element.
const kpiTemplates = new WeakMap<HTMLElement, Map<string, HTMLElement>>();

// --- Formatting helpers -----------------------------------------------------

function formatMoney(minor: number, currency: string): string {
  const major = minor / 100;
  const code = (currency || "").toUpperCase();
  if (!code) return new Intl.NumberFormat("sv-SE").format(major);
  try {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: code,
    }).format(major);
  } catch {
    return `${new Intl.NumberFormat("sv-SE").format(major)} ${code}`;
  }
}

function formatCount(n: number): string {
  return new Intl.NumberFormat("sv-SE").format(n);
}

function formatBucketLabel(iso: string, granularity: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const opts: Intl.DateTimeFormatOptions =
    granularity === "monthly"
      ? { month: "short", year: "numeric", timeZone: "UTC" }
      : { day: "numeric", month: "short", timeZone: "UTC" };
  return new Intl.DateTimeFormat("sv-SE", opts).format(d);
}

  // SEK first, then alphabetical.
function currencyRank(c: string): number {
  return c.toLowerCase() === "sek" ? 0 : 1;
}

function sortCurrencies(currencies: Iterable<string>): string[] {
  return Array.from(new Set(currencies)).sort(
    (a, b) => currencyRank(a) - currencyRank(b) || a.localeCompare(b),
  );
}

// --- Root + state -----------------------------------------------------------

interface StatsState {
  base: string;
  union: string;
  range: string;
  granularity: string;
}

function readState(root: HTMLElement): StatsState {
  return {
    base: root.dataset.endpointBase ?? "/Admin/statistics",
    union: root.dataset.union ?? "all",
    range: root.dataset.range ?? "30d",
    granularity: root.dataset.granularity ?? "daily",
  };
}

// 12m/YTD windows are computed client-side in UTC and passed as from/to; 30d is
// left to the server default.
function buildQuery(widget: string, state: StatsState): string {
  const params = new URLSearchParams();
  params.set("union", state.union || "all");
  params.set("granularity", state.granularity);

  const now = new Date();
  if (state.range === "12m") {
    const from = new Date(
      Date.UTC(now.getUTCFullYear() - 1, now.getUTCMonth(), now.getUTCDate()),
    );
    params.set("from", from.toISOString());
    params.set("to", now.toISOString());
  } else if (state.range === "ytd") {
    const from = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
    params.set("from", from.toISOString());
    params.set("to", now.toISOString());
  }
  return `${state.base}/${widget}?${params.toString()}`;
}

// --- Loader / error toggles (per card) --------------------------------------

function widgetCards(root: HTMLElement, widget: string): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>(
      `.stats-widget[data-widget="${widget}"]`,
    ),
  );
}

function setLoading(card: HTMLElement): void {
  card.querySelector<HTMLElement>("[data-loader]")?.removeAttribute("hidden");
  card.querySelector<HTMLElement>("[data-error]")?.setAttribute("hidden", "");
}

function clearLoader(card: HTMLElement): void {
  card.querySelector<HTMLElement>("[data-loader]")?.setAttribute("hidden", "");
}

function showError(card: HTMLElement, message: string): void {
  clearLoader(card);
  const error = card.querySelector<HTMLElement>("[data-error]");
  if (error) {
    error.textContent = message;
    error.removeAttribute("hidden");
  }
  // Hide any data surfaces in this card.
  card
    .querySelector<HTMLElement>("[data-kpi-grid]")
    ?.setAttribute("hidden", "");
  card
    .querySelectorAll<HTMLElement>("canvas[data-chart]")
    .forEach((c) => c.setAttribute("hidden", ""));
}

// --- Chart rendering --------------------------------------------------------

function renderChart(
  root: HTMLElement,
  name: string,
  config: ChartConfiguration,
): void {
  const canvas = root.querySelector<HTMLCanvasElement>(
    `canvas[data-chart="${name}"]`,
  );
  if (!canvas) return;
  charts.get(name)?.destroy();
  canvas.removeAttribute("hidden");
  charts.set(name, new Chart(canvas, config));
}

function uniqueBuckets(points: TimeBucketPoint[]): string[] {
  return Array.from(new Set(points.map((p) => p.bucketStartUtc))).sort();
}

function lineColor(i: number): string {
  return PALETTE[i % PALETTE.length];
}

// Sum a numeric field per bucket, ignoring currency (count series).
function countByBucket(points: TimeBucketPoint[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const p of points) {
    map.set(p.bucketStartUtc, (map.get(p.bucketStartUtc) ?? 0) + p.count);
  }
  return map;
}

function renderRevenue(
  root: HTMLElement,
  points: TimeBucketPoint[],
  granularity: string,
): void {
  const buckets = uniqueBuckets(points);
  const currencies = sortCurrencies(points.map((p) => p.currency));
  const labels = buckets.map((b) => formatBucketLabel(b, granularity));

  const datasets: ChartDataset<"line">[] = currencies.map((cur, i) => {
    const byBucket = new Map<string, number>();
    for (const p of points) {
      if (p.currency !== cur) continue;
      byBucket.set(p.bucketStartUtc, (byBucket.get(p.bucketStartUtc) ?? 0) + p.amount);
    }
    return {
      label: cur.toUpperCase(),
      data: buckets.map((b) => (byBucket.get(b) ?? 0) / 100),
      borderColor: lineColor(i),
      backgroundColor: `${lineColor(i)}22`,
      fill: true,
      tension: 0.25,
      pointRadius: 2,
    };
  });

  renderChart(root, "revenue", {
    type: "line",
    data: { labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: currencies.length > 1 },
        tooltip: {
          callbacks: {
            label: (ctx) =>
              `${ctx.dataset.label}: ${formatMoney(
                Math.round((ctx.parsed.y ?? 0) * 100),
                currencies[ctx.datasetIndex] ?? "",
              )}`,
          },
        },
      },
      scales: { y: { beginAtZero: true } },
    },
  });
}

function renderSubscriptionsTrend(
  root: HTMLElement,
  data: SubscriptionsResponse,
  granularity: string,
): void {
  const buckets = uniqueBuckets([
    ...data.newSubscriptions,
    ...data.canceledSubscriptions,
  ]);
  const labels = buckets.map((b) => formatBucketLabel(b, granularity));
  const created = countByBucket(data.newSubscriptions);
  const canceled = countByBucket(data.canceledSubscriptions);

  renderChart(root, "subscriptions-trend", {
    type: "bar",
    data: {
      labels,
      datasets: [
        {
          label: "Nya",
          data: buckets.map((b) => created.get(b) ?? 0),
          backgroundColor: PALETTE[2],
        },
        {
          label: "Avslutade",
          data: buckets.map((b) => canceled.get(b) ?? 0),
          backgroundColor: PALETTE[1],
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: true } },
      scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
    },
  });
}

function renderSubscriptionBreakdown(
  root: HTMLElement,
  breakdown: PriceBreakdownRow[],
): void {
  const sorted = [...breakdown].sort((a, b) => b.activeCount - a.activeCount);
  const labels = sorted.map((r) => r.productName ?? r.priceId);
  renderChart(root, "subscription-breakdown", {
    type: "doughnut",
    data: {
      labels,
      datasets: [
        {
          data: sorted.map((r) => r.activeCount),
          backgroundColor: sorted.map((_, i) => lineColor(i)),
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: "right" } },
    },
  });
}

function renderCustomers(
  root: HTMLElement,
  points: TimeBucketPoint[],
  granularity: string,
): void {
  const buckets = uniqueBuckets(points);
  const labels = buckets.map((b) => formatBucketLabel(b, granularity));
  const byBucket = countByBucket(points);
  renderChart(root, "customers", {
    type: "line",
    data: {
      labels,
      datasets: [
        {
          label: "Nya kunder",
          data: buckets.map((b) => byBucket.get(b) ?? 0),
          borderColor: PALETTE[0],
          backgroundColor: `${PALETTE[0]}22`,
          fill: true,
          tension: 0.25,
          pointRadius: 2,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
    },
  });
}

function renderRevenueByConnection(
  root: HTMLElement,
  rows: ConnectionRevenueRow[],
): void {
  const top = [...rows]
    .sort((a, b) => b.revenueInPeriod - a.revenueInPeriod)
    .slice(0, 10);
  const labels = top.map(
    (r) => `${r.slug ?? r.unionId} (${r.currency.toUpperCase()})`,
  );
  renderChart(root, "revenue-by-connection", {
    type: "bar",
    data: {
      labels,
      datasets: [
        {
          label: "Intäkt",
          data: top.map((r) => r.revenueInPeriod / 100),
          backgroundColor: top.map((_, i) => lineColor(i)),
        },
      ],
    },
    options: {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) =>
              formatMoney(
                Math.round((ctx.parsed.x ?? 0) * 100),
                top[ctx.dataIndex]?.currency ?? "",
              ),
          },
        },
      },
      scales: { x: { beginAtZero: true } },
    },
  });
}

// --- Summary / KPI cards ----------------------------------------------------

function captureTemplates(grid: HTMLElement): Map<string, HTMLElement> {
  let templates = kpiTemplates.get(grid);
  if (!templates) {
    templates = new Map<string, HTMLElement>();
    grid
      .querySelectorAll<HTMLElement>(".stat[data-kpi]")
      .forEach((stat) => {
        const key = stat.dataset.kpi;
        if (key) templates!.set(key, stat.cloneNode(true) as HTMLElement);
      });
    kpiTemplates.set(grid, templates);
  }
  return templates;
}

function makeStat(
  template: HTMLElement,
  labelSuffix: string,
  value: string,
): HTMLElement {
  const node = template.cloneNode(true) as HTMLElement;
  const labelEl = node.querySelector<HTMLElement>(".stat__label");
  const valueEl = node.querySelector<HTMLElement>("[data-kpi-value]");
  if (labelSuffix && labelEl) {
    labelEl.textContent = `${labelEl.textContent ?? ""} (${labelSuffix})`;
  }
  if (valueEl) valueEl.textContent = value;
  return node;
}

function kpiValue(group: CurrencyKpis, key: string): number {
  switch (key) {
    case "active-payers":
      return group.activePayers;
    case "mrr":
      return group.mrr;
    case "arpu":
      return group.arpu;
    case "gross-revenue":
      return group.grossRevenueInPeriod;
    case "new-subscriptions":
      return group.newSubscriptions;
    case "churn":
      return group.canceledSubscriptions;
    default:
      return 0;
  }
}

function renderSummary(root: HTMLElement, card: HTMLElement, data: SummaryResponse): void {
  const grid = card.querySelector<HTMLElement>("[data-kpi-grid]");
  if (!grid) return;
  const templates = captureTemplates(grid);

  const currencies = sortCurrencies(data.kpis.map((k) => k.currency).filter((c) => c !== ""));
  const byCurrency = new Map(data.kpis.map((k) => [k.currency, k]));
  const newCustomers = data.kpis.reduce((sum, k) => sum + k.newCustomers, 0);

  grid.replaceChildren();

  for (const [key, template] of templates) {
    if (key === "active-connections") {
      grid.appendChild(makeStat(template, "", formatCount(data.activeConnectionCount)));
    } else if (key === "new-customers") {
      grid.appendChild(makeStat(template, "", formatCount(newCustomers)));
    } else if (PER_CURRENCY_KPIS.has(key)) {
      if (currencies.length === 0) {
        grid.appendChild(makeStat(template, "", "—"));
        continue;
      }
      for (const cur of currencies) {
        const group = byCurrency.get(cur);
        if (!group) continue;
        const raw = kpiValue(group, key);
        const value = MONEY_KPIS.has(key) ? formatMoney(raw, cur) : formatCount(raw);
        grid.appendChild(makeStat(template, cur.toUpperCase(), value));
      }
    }
  }

  grid.removeAttribute("hidden");
  updateBanner(root, data);
}

function updateBanner(root: HTMLElement, data: SummaryResponse): void {
  const banner = root.querySelector<HTMLElement>("[data-stats-warning]");
  if (!banner) return;
  const messages: string[] = [];
  if (data.partialFailures.length > 0) {
    messages.push(
      `Delvis data: ${data.partialFailures.length} koppling(ar) kunde inte läsas.`,
    );
  }
  if (data.historyComplete === false) {
    messages.push("Historik byggs fortfarande upp — kör backfill för full täckning.");
  }
  if (messages.length === 0) {
    banner.setAttribute("hidden", "");
    banner.textContent = "";
    return;
  }
  banner.textContent = messages.join(" ");
  banner.removeAttribute("hidden");
}

// --- Fetch + dispatch per widget --------------------------------------------

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

async function loadWidget(
  root: HTMLElement,
  widget: string,
  state: StatsState,
): Promise<void> {
  const cards = widgetCards(root, widget);
  if (cards.length === 0) return;
  cards.forEach(setLoading);

  try {
    const url = buildQuery(widget, state);
    switch (widget) {
      case "summary": {
        const data = await fetchJson<SummaryResponse>(url);
        for (const card of cards) {
          clearLoader(card);
          renderSummary(root, card, data);
        }
        break;
      }
      case "revenue": {
        const data = await fetchJson<TimeBucketPoint[]>(url);
        cards.forEach(clearLoader);
        renderRevenue(root, data, state.granularity);
        break;
      }
      case "subscriptions": {
        const data = await fetchJson<SubscriptionsResponse>(url);
        cards.forEach(clearLoader);
        renderSubscriptionsTrend(root, data, state.granularity);
        renderSubscriptionBreakdown(root, data.breakdown);
        break;
      }
      case "customers": {
        const data = await fetchJson<TimeBucketPoint[]>(url);
        cards.forEach(clearLoader);
        renderCustomers(root, data, state.granularity);
        break;
      }
      case "revenue-by-connection": {
        const data = await fetchJson<ConnectionRevenueRow[]>(url);
        cards.forEach(clearLoader);
        renderRevenueByConnection(root, data);
        break;
      }
    }
  } catch {
    cards.forEach((card) => showError(card, "Kunde inte ladda data."));
  }
}

function loadAll(root: HTMLElement): void {
  const state = readState(root);
  for (const widget of WIDGETS) {
    void loadWidget(root, widget, state);
  }
}

// --- Controls ---------------------------------------------------------------

function wireControls(root: HTMLElement): void {
  const union = root.querySelector<HTMLSelectElement>("[data-stats-union]");
  union?.addEventListener("change", () => {
    root.dataset.union = union.value || "all";
    loadAll(root);
  });

  const rangeGroup = root.querySelector<HTMLElement>("[data-stats-range]");
  rangeGroup?.addEventListener("click", (event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
      "button[data-range]",
    );
    if (!button || !rangeGroup.contains(button)) return;

    rangeGroup
      .querySelectorAll<HTMLButtonElement>("button[data-range]")
      .forEach((b) => {
        const active = b === button;
        b.classList.toggle("is-active", active);
        b.setAttribute("aria-pressed", String(active));
      });

    root.dataset.range = button.dataset.range ?? "30d";
    root.dataset.granularity = button.dataset.granularity ?? "daily";
    loadAll(root);
  });
}

export function initStatistics(): void {
  const root = document.querySelector<HTMLElement>("[data-stats-root]");
  if (!root) return; // not on the Statistics page
  wireControls(root);
  loadAll(root);
}
